import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { Sparkles, Music, Crown, Check, Loader2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useT } from '@/context/LocaleContext';

const features = [
  'Maestro IA illimité (Claude Sonnet 5)',
  'Chansons avancées & pro débloquées',
  'Playlists illimitées',
  'Badge doré exclusif « Membre Pro »',
  'Nouvelles fonctionnalités en avant-première',
  'Soutien indépendant de la plateforme',
];

export default function Pro() {
  const { user, checkAuth } = useAuth();
  const { t } = useT();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState('maestro_pro_yearly');
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [launchCoupon, setLaunchCoupon] = useState(null);

  useEffect(() => {
    (async () => {
      const [plansRes, couponRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/coupon').catch(() => ({ data: null })),
      ]);
      setPlans(plansRes.data.plans);
      setLaunchCoupon(couponRes.data);
    })();
  }, []);

  const applyCoupon = () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    if (launchCoupon?.available && code === launchCoupon.code) {
      setCouponApplied({ code, percent_off: launchCoupon.percent_off });
      toast.success(`Code ${code} appliqué : -${launchCoupon.percent_off}% !`);
    } else {
      toast.error('Code invalide ou plus disponible');
    }
  };

  const upgrade = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/billing/checkout', {
        lookup_key: selected,
        origin_url: window.location.origin,
        coupon_code: couponApplied?.code,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error('Impossible de démarrer le paiement');
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!confirm('Annuler ton abonnement à la fin de la période en cours ?')) return;
    try {
      await api.post('/billing/cancel');
      toast.success('Abonnement annulé à la fin de la période.');
      await checkAuth();
    } catch { toast.error('Erreur'); }
  };

  const isPro = user?.plan?.is_pro;
  const monthly = plans.find(p => p.lookup_key === 'maestro_pro_monthly');
  const yearly = plans.find(p => p.lookup_key === 'maestro_pro_yearly');

  return (
    <Layout>
      <div className="p-6 md:p-12 max-w-5xl">
        <div className="mb-10 rise text-center">
          <div className="overline text-orange-400 mb-2 flex items-center justify-center gap-2">
            <Crown size={14} /> {t('pro.overline')}
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none">
            {t('pro.title1')}<br/>
            <em className="text-orange-500 not-italic">{t('pro.title2')}</em>
          </h1>
          <p className="text-stone-400 mt-4 max-w-xl mx-auto">{t('pro.free_trial')}</p>
        </div>

        {isPro ? (
          <div className="card-stone p-8 mb-10 text-center">
            <Crown className="mx-auto text-orange-400 mb-3" size={40} />
            <div className="font-display text-3xl">{t('pro.member')}</div>
            <div className="text-stone-400 mt-2">
              {t('pro.status')} : <span className="text-stone-200">{user?.plan?.status}</span>
              {user?.plan?.current_period_end && (
                <> · {t('pro.renewal')} {new Date(user.plan.current_period_end).toLocaleDateString()}</>
              )}
            </div>
            {!user?.plan?.cancel_at_period_end ? (
              <button data-testid="cancel-sub-btn" onClick={cancel} className="btn-ghost mt-6">{t('pro.cancel')}</button>
            ) : (
              <div className="mt-4 text-orange-400 text-sm">{t('pro.cancel_scheduled')}</div>
            )}
          </div>
        ) : (
          <>
            {launchCoupon?.available && (
              <div data-testid="founding-banner" className="mb-6 rounded-2xl border border-orange-800/60 bg-gradient-to-br from-orange-950/40 to-transparent p-4 text-center">
                <div className="text-orange-300 font-medium">
                  {t('pro.founding', { n: launchCoupon.remaining })}
                </div>
                <div className="text-stone-400 text-xs mt-1">Utilise le code <strong className="text-orange-400">{launchCoupon.code}</strong> ci-dessous</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {monthly && (
                <PlanCard
                  testid="plan-monthly"
                  selected={selected === monthly.lookup_key}
                  onClick={() => setSelected(monthly.lookup_key)}
                  title={t('pro.monthly')}
                  price={`${monthly.amount.toFixed(2)} €`}
                  period={t('pro.per_month')}
                  hint={t('pro.flexible')}
                  discount={couponApplied ? (monthly.amount * (1 - couponApplied.percent_off / 100)).toFixed(2) + ' €' : null}
                />
              )}
              {yearly && (
                <PlanCard
                  testid="plan-yearly"
                  selected={selected === yearly.lookup_key}
                  onClick={() => setSelected(yearly.lookup_key)}
                  title={t('pro.yearly')}
                  price={`${yearly.amount.toFixed(2)} €`}
                  period={t('pro.per_year')}
                  hint={monthly ? `${t('pro.save')} ${Math.round(100 - (yearly.amount/12) / monthly.amount * 100)}%` : ''}
                  ribbon={t('pro.best_offer')}
                  discount={couponApplied ? (yearly.amount * (1 - couponApplied.percent_off / 100)).toFixed(2) + ' €' : null}
                />
              )}
            </div>

            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-2 max-w-md w-full">
                <Tag size={16} className="text-stone-500" />
                <input
                  data-testid="coupon-input"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder={t('pro.coupon_placeholder')}
                  disabled={!!couponApplied}
                  className="flex-1 px-3 py-2 rounded-full bg-stone-900 border border-stone-800 text-sm text-stone-100 focus:outline-none focus:border-orange-800 disabled:opacity-50"
                />
                {couponApplied ? (
                  <button data-testid="coupon-remove" onClick={() => { setCouponApplied(null); setCouponCode(''); }} className="btn-ghost text-xs">✕</button>
                ) : (
                  <button data-testid="coupon-apply" onClick={applyCoupon} className="btn-ghost text-xs">{t('pro.coupon_apply')}</button>
                )}
              </div>
            </div>

            <button
              data-testid="upgrade-btn"
              onClick={upgrade}
              disabled={busy}
              className="btn-primary w-full md:w-auto md:min-w-[280px] flex items-center justify-center gap-2 mx-auto disabled:opacity-50 mb-10"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
              {t('pro.start_trial')}
            </button>
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-stone-800 bg-stone-900/40">
              <Check size={18} className="text-orange-400 mt-0.5 shrink-0" />
              <div className="text-stone-200 text-sm">{f}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center text-stone-500 text-xs">
          <Music size={14} className="inline mr-1" /> Paiement sécurisé par Stripe
        </div>
      </div>
    </Layout>
  );
}

function PlanCard({ testid, title, price, period, hint, selected, onClick, ribbon, discount }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative text-left p-6 rounded-2xl border transition-all ${
        selected ? 'border-orange-600 bg-orange-950/20' : 'border-stone-800 bg-stone-900/40 hover:border-stone-700'
      }`}
    >
      {ribbon && (
        <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-orange-700 text-white overline">
          {ribbon}
        </div>
      )}
      <div className="overline text-stone-500">{title}</div>
      <div className="mt-2 font-display text-5xl">
        {discount ? (
          <>
            <span className="line-through text-stone-500 text-2xl mr-2">{price}</span>
            <span className="text-orange-400">{discount}</span>
          </>
        ) : (
          price
        )}
        <span className="text-lg text-stone-400 font-body font-normal ml-1">{period}</span>
      </div>
      <div className="text-stone-400 text-sm mt-2">{hint}</div>
    </button>
  );
}
